import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Divider,
  Alert,
  AlertTitle,
  CircularProgress,
  Tabs,
  Tab,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  AccountBalance,
  TrendingUp,
  Schedule,
  CheckCircle,
  HourglassEmpty,
  Info,
  Refresh,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface Job {
  id: string;
  jobDate: string;
  customerName: string;
  serviceType: string;
  address: string;
  amount: number;
  yourEarnings: number;
  paymentDate: string;
  settlementStatus: string;
  settledDate?: string;
  isReadyForPayout: boolean;
  reasonNotReady: string;
}

interface PayoutHistoryItem {
  id: string;
  weekStart: string;
  weekEnd: string;
  totalJobs: number;
  totalEarnings: number;
  status: string;
  processedDate: string;
  paidDate?: string;
}

interface EarningsData {
  isFirstPayout: boolean;
  nextPayoutDate: string;
  ready: {
    jobs: Job[];
    totalJobs: number;
    totalEarnings: number;
  };
  processing: {
    jobs: Job[];
    totalJobs: number;
    totalEarnings: number;
  };
  history: PayoutHistoryItem[];
  summary: {
    lifetimeEarnings: number;
    totalPayouts: number;
    pendingEarnings: number;
    nextPayoutAmount: number;
  };
  payoutSchedule: {
    frequency: string;
    day: string;
    nextDate: string;
    firstPayoutHold: string | null;
    ongoingSpeed: string;
  };
}

interface EarningsBreakdownData {
  earnings: {
    total: number;
    fromStandardFeeCustomers: number;
    fromImportedCustomers: number;
    breakdown: {
      standardFeeJobs: number;
      importedFeeJobs: number;
    };
  };
}

const ProviderEarningsPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [earningsData, setEarningsData] = useState<EarningsData | null>(null);
  const [breakdownData, setBreakdownData] = useState<EarningsBreakdownData | null>(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownPeriod, setBreakdownPeriod] = useState<'all-time' | 'ytd' | 'monthly'>('all-time');
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    fetchEarningsData();
  }, []);

  useEffect(() => {
    fetchEarningsBreakdown();
  }, [breakdownPeriod]);

  const fetchEarningsBreakdown = async () => {
    try {
      setBreakdownLoading(true);
      const params = breakdownPeriod !== 'all-time' ? `?period=${breakdownPeriod}` : '';
      const response = await apiService.get(`/provider-performance${params}`);
      if (response.success && response.data) {
        const d = response.data as EarningsBreakdownData;
        setBreakdownData({ earnings: d.earnings });
      }
    } catch (err) {
      console.error('Error fetching earnings breakdown:', err);
    } finally {
      setBreakdownLoading(false);
    }
  };

  const fetchEarningsData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get('/providers/earnings/status');

      if (response.success && response.data) {
        setEarningsData(response.data as EarningsData);
      } else {
        setError(response.error || 'Failed to load earnings data');
      }
    } catch (err: any) {
      console.error('Error fetching earnings data:', err);
      setError(err.message || 'Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'processed':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading your earnings...</Typography>
      </Container>
    );
  }

  if (error || !earningsData) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error || 'Failed to load earnings data'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          My Earnings
        </Typography>
        <Tooltip title="Refresh data">
          <IconButton onClick={fetchEarningsData}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {/* First Payout Notice */}
      {earningsData.isFirstPayout && (
        <Alert severity="info" icon={<Schedule />} sx={{ mb: 3 }}>
          <AlertTitle>Welcome! Your First Payout</AlertTitle>
          Your first payout has a 7-day security hold. After that, funds are transferred to your Stripe Connect account every Friday once payments are settled with Stripe (typically 1-3 days after job completion). Complete Stripe Connect onboarding, add your bank account in Stripe Express, and submit a valid W-9 to receive payouts.
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircle color="success" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Next Payout
                </Typography>
              </Box>
              <Typography variant="h5" color="success.main">
                {formatCurrency(earningsData.summary.nextPayoutAmount)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Weekly on Fridays
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <HourglassEmpty color="warning" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Processing
                </Typography>
              </Box>
              <Typography variant="h5" color="warning.main">
                {formatCurrency(earningsData.processing.totalEarnings)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {earningsData.processing.totalJobs} jobs settling
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Schedule color="primary" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Pending Total
                </Typography>
              </Box>
              <Typography variant="h5">
                {formatCurrency(earningsData.summary.pendingEarnings)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Ready + Processing
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUp color="success" sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Lifetime Earnings
                </Typography>
              </Box>
              <Typography variant="h5">
                {formatCurrency(earningsData.summary.lifetimeEarnings)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {earningsData.summary.totalPayouts} payouts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Earnings Breakdown */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">Earnings Breakdown</Typography>
          <Tabs
            value={breakdownPeriod}
            onChange={(_e, v) => setBreakdownPeriod(v)}
            sx={{ minHeight: 'auto' }}
          >
            <Tab label="All-Time" value="all-time" />
            <Tab label="Year-to-Date" value="ytd" />
            <Tab label="This Month" value="monthly" />
          </Tabs>
        </Box>
        <Divider sx={{ mb: 3 }} />
        {breakdownLoading && !breakdownData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : breakdownData ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">New Platform Leads</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(breakdownData.earnings.fromStandardFeeCustomers)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {breakdownData.earnings.breakdown.standardFeeJobs} jobs completed
                  </Typography>
                  <Chip label="New Customers" size="small" color="primary" sx={{ fontWeight: 'bold' }} />
                </Box>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Imported Customers</Typography>
                  <Typography variant="body2" fontWeight="bold" color="success.main">
                    {formatCurrency(breakdownData.earnings.fromImportedCustomers)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {breakdownData.earnings.breakdown.importedFeeJobs} jobs completed
                  </Typography>
                  <Chip label="Existing Customers" size="small" color="success" sx={{ fontWeight: 'bold' }} />
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1" fontWeight="bold">
                  Total Earnings
                </Typography>
                <Typography variant="body1" fontWeight="bold" color="primary">
                  {formatCurrency(breakdownData.earnings.total)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                See how much you earned from new LawnHub marketplace customers (5% fee on first season) versus customers you imported (1% fee). Payout timing and job-level detail are in the tabs below.
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Earnings breakdown is not available right now.
          </Typography>
        )}
      </Paper>

      {/* Payout Schedule Info */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.50' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalance color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              Payout Schedule: {earningsData.payoutSchedule.frequency} Transfers on {earningsData.payoutSchedule.day}s
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Funds are transferred to your Stripe Connect account once payments settle (typically 1-3 days after job completion). {earningsData.payoutSchedule.firstPayoutHold ? `First payout: ${earningsData.payoutSchedule.firstPayoutHold} hold. ` : ''}After transfer, funds follow your Stripe payout schedule (configure in your Stripe Express dashboard).
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              <strong>To receive payouts:</strong> Complete Stripe Connect onboarding, add your bank account in Stripe Express, and submit a valid W-9. See your Provider Agreement for backup withholding (24%) if your TIN is missing or invalid.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab 
            label={`Ready for Payout (${earningsData.ready.totalJobs})`} 
            icon={<CheckCircle />}
            iconPosition="start"
          />
          <Tab 
            label={`Processing (${earningsData.processing.totalJobs})`} 
            icon={<HourglassEmpty />}
            iconPosition="start"
          />
          <Tab 
            label="Payout History" 
            icon={<AccountBalance />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {tabValue === 0 && (
        <Paper>
          {earningsData.ready.totalJobs === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No jobs ready for payout yet. Complete some jobs and they'll appear here!
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ p: 2, bgcolor: 'success.50', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" fontWeight="bold">
                  Ready for Next Payout: {earningsData.ready.totalJobs} jobs
                </Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(earningsData.ready.totalEarnings)}
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell align="right">Job Amount</TableCell>
                      <TableCell align="right">Your Earnings</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {earningsData.ready.jobs.map((job) => (
                      <TableRow key={job.id} hover>
                        <TableCell>{formatDate(job.jobDate)}</TableCell>
                        <TableCell>{job.customerName}</TableCell>
                        <TableCell>{job.serviceType}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {job.address}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatCurrency(job.amount)}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            {formatCurrency(job.yourEarnings)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label="Ready" size="small" color="success" />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={5} align="right">
                        <Typography variant="body2" fontWeight="bold">
                          TOTAL:
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="bold" color="success.main">
                          {formatCurrency(earningsData.ready.totalEarnings)}
                        </Typography>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      )}

      {tabValue === 1 && (
        <Paper>
          {earningsData.processing.totalJobs === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No payments currently processing.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ p: 2, bgcolor: 'warning.50', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" fontWeight="bold">
                  Processing: {earningsData.processing.totalJobs} jobs
                </Typography>
                <Typography variant="h6" color="warning.main">
                  {formatCurrency(earningsData.processing.totalEarnings)}
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Customer</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell align="right">Your Earnings</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {earningsData.processing.jobs.map((job) => (
                      <TableRow key={job.id} hover>
                        <TableCell>{formatDate(job.jobDate)}</TableCell>
                        <TableCell>{job.customerName}</TableCell>
                        <TableCell>{job.serviceType}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(job.yourEarnings)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={job.reasonNotReady}>
                            <Chip 
                              label={job.settlementStatus === 'settled' ? 'Security Hold' : 'Settling'} 
                              size="small" 
                              color="warning"
                              icon={<Info />}
                            />
                          </Tooltip>
                          <Typography variant="caption" display="block" color="text.secondary">
                            {job.reasonNotReady}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      )}

      {tabValue === 2 && (
        <Paper>
          {earningsData.history.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No payout history yet. Your payouts will appear here once processed.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Week</TableCell>
                    <TableCell align="center">Jobs</TableCell>
                    <TableCell align="right">Earnings</TableCell>
                    <TableCell>Processed</TableCell>
                    <TableCell>Paid</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {earningsData.history.map((payout) => (
                    <TableRow key={payout.id} hover>
                      <TableCell>
                        {formatDate(payout.weekStart)} - {formatDate(payout.weekEnd)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={payout.totalJobs} size="small" color="primary" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(payout.totalEarnings)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDate(payout.processedDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {payout.paidDate ? (
                          <Typography variant="caption">
                            {formatDate(payout.paidDate)}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Pending
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={payout.status} 
                          size="small" 
                          color={getStatusColor(payout.status)}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Container>
  );
};

export default ProviderEarningsPage;

